import BookmarkPlusDefaultIcon from '@/assets/images/icons/bookmark-plus-default.svg'
import BookmarkPlusHoverIcon from '@/assets/images/icons/bookmark-plus-hover.svg'
import BookmarkPlusPressedIcon from '@/assets/images/icons/bookmark-plus-pressed.svg'
import PlayIcon from '@/assets/images/icons/play-icon.svg'
import StopwatchIcon from '@/assets/images/icons/stopwatch_icon.svg'
import { BookmarkSave } from '@/components/BookmarkSave'
import { DASHBOARD_TOOLBAR_ARTWORK_PX } from '@/lib/dashboardToolbarUi'
import ExternalLink from '@/components/ExternalLink'
import { Lesson } from '@/types'
import { LinearGradient } from 'expo-linear-gradient'
import React, { useState } from 'react'
import { Platform } from 'react-native'
import { Button, SizableText, XStack, useMedia } from 'tamagui'
import { EyebrowText } from '@/components/ui/Typography'

interface LessonActionsProps {
  onPlayPress: () => void
  onSharePress: () => void
  style?: any
  isBookmarked?: boolean
  lesson?: Lesson | null
  duration?: number
  lessonId?: number
  courseId?: number
  collectionId?: number
  lessonUrl?: string
  // Receives final bookmarked state after save
  onBookmarkSave?: (isBookmarked: boolean) => void
  onBookmarkIconPress?: () => void
  showError: (msg: string) => void
  showSuccess: (msg: string) => void
  publishedAt?: string
  isAnon?: boolean
}

export const LessonActions: React.FC<LessonActionsProps> = ({
  onPlayPress,
  onSharePress,
  style,
  isBookmarked = false,
  lesson,
  duration,
  lessonId,
  courseId,
  collectionId,
  lessonUrl,
  onBookmarkSave,
  onBookmarkIconPress,
  showError,
  showSuccess,
  isAnon = false,
}) => {
  const media = useMedia()
  const [isBookmarkHovered, setIsBookmarkHovered] = useState(false)
  
  // Derive values from lesson when available, but allow explicit overrides
  const effectiveLessonId = typeof lessonId === 'number' ? lessonId : lesson?.id
  const effectiveCourseId = typeof courseId === 'number' ? courseId : lesson?.course_id
  const effectiveDuration = typeof duration === 'number' ? duration : lesson?.duration
  const effectiveVideoCount = lesson?.video_count ?? 0

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const totalMinutes = Math.floor(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
  };

  const durationOrVideoLabel =
    effectiveVideoCount > 1
      ? `${effectiveVideoCount} videos`
      : formatDuration(effectiveDuration)
        ? `${formatDuration(effectiveDuration)} Min`
        : '—';

  return (
    <XStack 
      justifyContent="space-between" 
      alignItems="center"
      style={style}
      // bg={"red"}
      width="100%"
    >
      <XStack alignItems="center" gap="$4">
        <ExternalLink url={lessonUrl || '#'} onPress={onPlayPress}>
          <LinearGradient
            colors={['#FF942F', '#FF2273']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: 20,
              paddingHorizontal: 15,
              paddingVertical: 8,
              minWidth: 90,
            }}
          >
            <SizableText color="white" 
              marginLeft="$2" 
              marginRight="$2"              
              fontWeight="400"
              fontSize="$3"
              $gtMd={{ fontSize: "$4"}}
              >
              Play
            </SizableText>
            <PlayIcon width={10} />
          </LinearGradient>
        </ExternalLink>

      <XStack alignItems="center" gap="$1.5">
        {effectiveVideoCount <= 1 && (
          <StopwatchIcon
            width={media.gtMd ? 20 : 16}
            height={media.gtMd ? 20 : 16}
          />
        )}
        <EyebrowText color="#0D0D0D">
          {durationOrVideoLabel}
        </EyebrowText>
      </XStack>
    </XStack>

      {!isAnon && typeof effectiveLessonId === 'number' && lessonUrl && (
        <BookmarkSave
          mode="lesson"
          onSave={onBookmarkSave || (() => {})}
          showError={showError}
          showSuccess={showSuccess}
          lessonId={effectiveLessonId}
          lessonUrl={lessonUrl}
          courseId={effectiveCourseId}
          collectionId={collectionId}
          onIconPress={onBookmarkIconPress}
          placement="left"
        >
          <Button
            circular
            size="$4"
            $gtMd={{ size: '$3.5' }}
            onMouseEnter={Platform.OS === 'web' ? () => setIsBookmarkHovered(true) : undefined}
            onMouseLeave={Platform.OS === 'web' ? () => setIsBookmarkHovered(false) : undefined}
          >
            {isBookmarked ? (
              <BookmarkPlusPressedIcon
                width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                height={DASHBOARD_TOOLBAR_ARTWORK_PX}
              />
            ) : isBookmarkHovered ? (
              <BookmarkPlusHoverIcon
                width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                height={DASHBOARD_TOOLBAR_ARTWORK_PX}
              />
            ) : (
              <BookmarkPlusDefaultIcon
                width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                height={DASHBOARD_TOOLBAR_ARTWORK_PX}
              />
            )}
          </Button>
        </BookmarkSave>
      )}
   
    </XStack>
  );
};

